import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { usePageDirection } from '../../hooks/useDirection.js';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../shadcn/index.js';

import { Button } from './Button.js';

export interface ConfirmOptions {
  /** The question/body shown to the user. */
  message: string;
  /** Optional heading; defaults to a generic "Please confirm". */
  title?: string;
  /** Override the confirm button label. */
  confirmLabel?: string;
  /** Override the cancel button label. */
  cancelLabel?: string;
  /** 'danger' renders a red confirm button for destructive actions. */
  tone?: 'default' | 'danger';
}

type Pending = ConfirmOptions & { resolve: (value: boolean) => void };

// The mounted <ConfirmHost /> registers a setter here so confirmDialog() can be
// called imperatively from anywhere (event handlers, plain functions).
let push: ((p: Pending) => void) | null = null;

/**
 * Async replacement for window.confirm. Resolves true when the user confirms,
 * false on cancel/dismiss. If the host isn't mounted yet (very early boot),
 * it falls back to the native confirm so a caller never hangs.
 */
export function confirmDialog(options: ConfirmOptions): Promise<boolean> {
  return new Promise((resolve) => {
    if (!push) {
      resolve(window.confirm(options.message));
      return;
    }
    push({ ...options, resolve });
  });
}

/** Mount once at the app root — renders the active confirm dialog. */
export function ConfirmHost(): JSX.Element {
  const { t } = useTranslation();
  // Mounted at the root, outside the pages that ask for it.
  const dir = usePageDirection();
  const [pending, setPending] = useState<Pending | null>(null);

  useEffect(() => {
    push = (p) =>
      setPending((prev) => {
        // A new request supersedes an unanswered one (cancel the old).
        prev?.resolve(false);
        return p;
      });
    return () => {
      push = null;
    };
  }, []);

  const settle = (result: boolean): void => {
    setPending((cur) => {
      cur?.resolve(result);
      return null;
    });
  };

  return (
    <Dialog
      open={!!pending}
      onOpenChange={(open) => {
        if (!open) settle(false);
      }}
    >
      {pending && (
        <DialogContent className="max-w-md" dir={dir}>
          <DialogHeader>
            <DialogTitle>
              {pending.title ?? t('confirmTitle', { defaultValue: 'Please confirm' })}
            </DialogTitle>
            <DialogDescription className="whitespace-pre-wrap">{pending.message}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="secondary" onClick={() => settle(false)}>
              {pending.cancelLabel ?? t('actions.cancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button
              variant={pending.tone === 'danger' ? 'danger' : 'primary'}
              onClick={() => settle(true)}
              autoFocus
            >
              {pending.confirmLabel ?? t('actions.confirm', { defaultValue: 'Confirm' })}
            </Button>
          </DialogFooter>
        </DialogContent>
      )}
    </Dialog>
  );
}
